<template>
  <div class="email-preview">
    <!-- Toolbar: left-aligned under the page tabs, wraps on narrow screens -->
    <div class="preview-toolbar">
      <ConsoleSegmentedToggle
          :model-value="viewMode"
          :options="[{ label: 'Desktop', value: 'desktop' }, { label: 'Mobile', value: 'mobile' }]"
          size="sm"
          @update:model-value="(mode) => setViewMode(String(mode))"
      />

      <AppButton
          v-if="email?.sections?.length"
          small
          :variant="showSections ? 'primary' : 'quiet'"
          :aria-pressed="showSections"
          @click="toggleSections"
      >
        <UIcon name="i-heroicons-view-columns" class="h-4 w-4"/>
        {{ showSections ? 'Hide sections' : 'Show sections' }}
      </AppButton>

      <USelect
          v-model="client"
          :items="clientItems"
          size="sm"
          aria-label="Email client to preview"
          class="min-w-[180px]"
          :ui="{ base: 'rounded-full' }"
      />

      <AppButton small :disabled="!builder?.html" @click="takeScreenshots">
        <UIcon name="i-heroicons-camera" class="h-4 w-4"/>
        Screenshots
      </AppButton>
    </div>

    <AppDialog v-model:open="showShots" :title="`How email clients show the ${viewMode} version`" wide="full">
      <label class="flex cursor-pointer items-center gap-2">
        <input v-model="markChanges" type="checkbox" style="accent-color:var(--accent);">
        Mark what each client changes
      </label>
      <div class="flex items-start gap-4 overflow-x-auto" style="padding-bottom:4px;">
        <div v-for="shot in shots" :key="shot.client" class="shot">
          <div class="flex items-center justify-between gap-2">
            <span style="font-weight:600;">{{ shot.label }}</span>
            <a
                v-if="shot.url"
                :href="shot.url"
                :download="`${email?.alias || 'email'}-${shot.client}-${viewMode}.png`"
                style="color:var(--acctext); font-size:13px;"
            >
              Download
            </a>
          </div>
          <p v-if="shot.diff?.same" class="shot-note">Identical to the design</p>
          <template v-else-if="shot.url">
            <p v-if="shot.diff" style="margin:0; color:var(--warn); font-size:13px;">{{ describeDiff(shot.diff) }}</p>
            <a :href="shot.url" target="_blank" rel="noopener" class="relative block">
              <img :src="shot.url" :alt="`${shot.label} screenshot`" class="shot-img">
              <img
                  v-if="markChanges && shot.overlayUrl"
                  :src="shot.overlayUrl"
                  alt=""
                  class="pointer-events-none absolute left-0 top-0 w-full"
              >
            </a>
          </template>
          <p v-else-if="shot.error" style="margin:0; color:var(--bad); font-size:13px;">{{ shot.error }}</p>
          <div v-else class="shot-note">
            <UIcon class="h-5 w-5 animate-spin" name="i-heroicons-arrow-path"/>
          </div>
        </div>
      </div>
    </AppDialog>

    <ul v-if="emulated.notes.length" class="mb-4 flex flex-col gap-1" style="color:var(--warn); font-size:13px;">
      <li v-for="note in emulated.notes" :key="note">{{ note }}</li>
    </ul>

    <!-- Email Preview Container -->
    <div class="preview-well">
      <div v-if="builder?.html" class="overflow-hidden relative">
        <iframe
            ref="previewIframe"
            :srcdoc="getProcessedHtml()"
            :style="containerStyles"
            :width="containerWidth"
            class="mx-auto bg-white"
            frameborder="0"
            scrolling="no"
            @load="resizeIframe"
        />
      </div>
      <div v-else class="h-96 flex items-center justify-center">
        <UIcon class="animate-spin h-8 w-8" style="color:var(--muted);" name="i-heroicons-arrow-path"/>
        <span class="ml-3" style="color:var(--muted);">Loading preview…</span>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import type { ModelRef } from "vue"
import { EMAIL_CLIENTS, emulateClient, type EmailClientId } from "#shared/emailClients/emulate"

const email = defineModel<EmailT>("email")
const group = defineModel<EmailGroupT>("group")

const emit = defineEmits<{
    'edit-link': [sectionIndex: number, imageIndex: number]
}>()

if (!email.value) {
    throw new Error("Email is required")
}

if (!group.value) {
    throw new Error("Group is required")
}

// Email preview settings
const viewMode = ref("desktop")
const editorPreferences = useEditorPreferencesStore()
const showSections = computed({
    get: () => editorPreferences.showSections,
    set: (value: boolean) => editorPreferences.setShowSections(value)
})
const previewIframe = ref<HTMLIFrameElement | null>(null)
const containerWidth = computed(() => viewMode.value === "desktop" ? "720" : "375")
const containerStyles = computed(() => ({
    width: `${containerWidth.value}px`,
    margin: "0 auto",
    padding: "0",
    maxWidth: "100%",
    border: "none",
    transition: "width 0.15s ease-in-out"
}))

// Build the email HTML with preview flag
const builder = await useEmailBuilder({email, group, isPreview: true} as { email: ModelRef<EmailT>, group: ModelRef<EmailGroupT>, isPreview: boolean })

// Show the email as a given client would: its unsupported CSS removed, its quirks applied.
const client = ref<EmailClientId | "none">("none")
const clientItems = [
    {label: "As designed", value: "none"},
    ...Object.entries(EMAIL_CLIENTS).map(([value, label]) => ({label, value}))
]
// Clients receive the exported HTML, which has its CSS inlined, so emulate that.
const sentHtml = builder?.html ? await useCssInline().inlineCss(builder.html) : ""
const emulated = computed(() => {
    if (client.value === "none" || !sentHtml) return {html: builder?.html ?? "", notes: [] as string[]}
    return emulateClient(sentHtml, client.value)
})

// Real PNGs of every client, rendered server-side by Cloudflare Browser Rendering.
interface Shot { client: string, label: string, url?: string, error?: string, diff?: ScreenshotDiff, overlayUrl?: string }
const showShots = ref(false)
const shots = ref<Shot[]>([])
const markChanges = ref(true)

function describeDiff(diff: ScreenshotDiff): string {
    const parts = [diff.percent < 0.01 ? "Under 0.01% of pixels differ" : `${diff.percent.toFixed(2)}% of pixels differ`]
    if (diff.heightDelta) parts.push(`${Math.abs(diff.heightDelta)}px ${diff.heightDelta > 0 ? "taller" : "shorter"}`)
    return parts.join(", ")
}

let shotRun = 0

async function takeScreenshots() {
    const html = sentHtml
    if (!html) return
    shots.value.forEach((shot) => {
        if (shot.url) URL.revokeObjectURL(shot.url)
        if (shot.overlayUrl) URL.revokeObjectURL(shot.overlayUrl)
    })
    let designed: Blob | undefined
    const run = ++shotRun
    shots.value = clientItems.map(({label, value}): Shot => ({client: value, label}))
    showShots.value = true
    // Loop over shots.value, not the array assigned to it: only the reactive copies it hands
    // back update the modal when they change.
    // One at a time: Browser Rendering's free tier allows only a few requests a minute.
    for (const shot of shots.value) {
        try {
            const png = await $fetch<Blob>("/api/v1/preview/screenshot", {
                method: "POST",
                body: {html, client: shot.client, width: Number(containerWidth.value)},
                responseType: "blob"
            })
            shot.url = URL.createObjectURL(png)
            if (shot.client === "none") designed = png
            else if (designed) {
                // A failed comparison should not cost the screenshot itself.
                try {
                    shot.diff = await diffScreenshots(designed, png)
                    if (shot.diff.overlay) shot.overlayUrl = URL.createObjectURL(shot.diff.overlay)
                } catch (error) {
                    console.error("Could not compare screenshots:", error)
                }
            }
        } catch (error) {
            const e = error as { statusMessage?: string, message?: string }
            shot.error = e.statusMessage || e.message || "Screenshot failed"
        }
        // A newer click replaced this run.
        if (run !== shotRun) return
    }
}

// Toggle sections overlay
function toggleSections() {
    showSections.value = !showSections.value
}

// Process HTML to add section indicators
function getProcessedHtml() {
    if (!builder?.html) {
        return ''
    }

    let processedHtml = emulated.value.html
    const sections = email.value?.sections || []
    const pinnedClass = showSections.value ? ' class="sections-pinned"' : ''

    // Inject styles for section and image indicators.
    // Markers are visible whenever the body is hovered, or when pinned via the toggle.
    const sectionStyles = `
        <style>
            /* Section styles */
            .email-section {
                position: relative !important;
                display: block !important;
            }
            .email-section.show-sections::after {
                content: "" !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                border: 2px solid #8fc7e8 !important;
                pointer-events: none !important;
                z-index: 999 !important;
                opacity: 0 !important;
                transition: opacity 0.15s ease-in-out !important;
            }
            body:hover .email-section.show-sections::after,
            body.sections-pinned .email-section.show-sections::after {
                opacity: 1 !important;
            }

            /* Image styles */
            .email-image {
                position: relative !important;
                display: inline-block !important;
            }
            .email-image.show-images::after {
                content: "" !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                outline: 2px dashed #ff5fa8 !important;
                outline-offset: -2px !important;
                pointer-events: none !important;
                z-index: 998 !important;
                opacity: 0 !important;
                transition: opacity 0.15s ease-in-out !important;
            }
            body:hover .email-image.show-images::after,
            body.sections-pinned .email-image.show-images::after {
                opacity: 1 !important;
            }
            .email-image.show-images::before {
                content: "Section " attr(data-section) ", image " attr(data-image) !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                transform: translateY(-100%) !important;
                background: #1e1a14 !important;
                color: #f1eadd !important;
                padding: 3px 8px !important;
                border-radius: 6px 6px 0 0 !important;
                font-size: 12px !important;
                font-weight: 600 !important;
                line-height: 1.4 !important;
                z-index: 1000 !important;
                font-family: system-ui, -apple-system, sans-serif !important;
                pointer-events: none !important;
                box-shadow: 0 -2px 4px rgba(0,0,0,0.2) !important;
                opacity: 0 !important;
                transition: opacity 0.15s ease-in-out !important;
                white-space: nowrap !important;
            }
            body:hover .email-image.show-images::before,
            body.sections-pinned .email-image.show-images::before {
                opacity: 1 !important;
            }
            .email-image.show-images[data-link]:not([data-link=""]) {
                position: relative !important;
            }
            .email-image.show-images[data-link]:not([data-link=""]) > a::after {
                content: attr(href) !important;
                position: absolute !important;
                top: 0 !important;
                right: 0 !important;
                transform: translateY(-100%) !important;
                background: #ffb224 !important;
                color: #1a1408 !important;
                padding: 3px 8px !important;
                border-radius: 6px 6px 0 0 !important;
                font-size: 12px !important;
                line-height: 1.4 !important;
                z-index: 1000 !important;
                font-family: system-ui, -apple-system, sans-serif !important;
                max-width: 70% !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
                pointer-events: none !important;
                box-shadow: 0 -2px 4px rgba(0,0,0,0.2) !important;
                opacity: 0 !important;
                transition: opacity 0.15s ease-in-out !important;
            }
            body:hover .email-image.show-images[data-link]:not([data-link=""]) > a::after,
            body.sections-pinned .email-image.show-images[data-link]:not([data-link=""]) > a::after {
                opacity: 1 !important;
            }
        </style>
    `

    // Insert styles before closing head tag or at the beginning
    if (processedHtml.includes('</head>')) {
        processedHtml = processedHtml.replace('</head>', sectionStyles + '</head>')
    } else {
        processedHtml = sectionStyles + processedHtml
    }

    // Add the show classes to sections and images
    processedHtml = processedHtml.replace(/class="email-section"/g, 'class="email-section show-sections"')
    processedHtml = processedHtml.replace(/class="email-image"/g, 'class="email-image show-images"')

    // Apply pinned state to body so the overlays stay visible without hover
    if (pinnedClass) {
        if (/<body[^>]*>/i.test(processedHtml)) {
            processedHtml = processedHtml.replace(/<body([^>]*)>/i, (_m, attrs) => {
                const classMatch = /class="([^"]*)"/.exec(attrs)
                if (classMatch) {
                    return `<body${attrs.replace(/class="([^"]*)"/, `class="$1 sections-pinned"`)}>`
                }
                return `<body${attrs} class="sections-pinned">`
            })
        } else {
            processedHtml = `<body class="sections-pinned">${processedHtml}</body>`
        }
    }

    // Inject a click handler that intercepts link clicks inside email-image wrappers
    // and posts a message to the parent so we can focus the matching form input.
    const clickScript = `
        <script>
            document.addEventListener('click', function (e) {
                var wrapper = e.target.closest && e.target.closest('.email-image');
                if (!wrapper) return;
                var anchor = e.target.closest('a');
                if (!anchor || !wrapper.contains(anchor)) return;
                e.preventDefault();
                e.stopPropagation();
                var section = parseInt(wrapper.getAttribute('data-section'), 10);
                var image = parseInt(wrapper.getAttribute('data-image'), 10);
                if (isNaN(section) || isNaN(image)) return;
                window.parent.postMessage({
                    type: 'email-preview-edit-link',
                    sectionIndex: section - 1,
                    imageIndex: image - 1
                }, '*');
            }, true);
        <\/script>
    `

    if (processedHtml.includes('</body>')) {
        processedHtml = processedHtml.replace('</body>', clickScript + '</body>')
    } else {
        processedHtml = processedHtml + clickScript
    }

    return processedHtml
}

function handlePreviewMessage(event: MessageEvent) {
    const data = event.data
    if (!data || data.type !== 'email-preview-edit-link') return
    if (typeof data.sectionIndex !== 'number' || typeof data.imageIndex !== 'number') return
    emit('edit-link', data.sectionIndex, data.imageIndex)
}

// Function to resize iframe based on content height
function resizeIframe() {
    if (!previewIframe.value) return

    try {
        // Reset height to handle resize properly
        previewIframe.value.style.height = "10px"

        // Get the document inside iframe
        const iframeDoc = previewIframe.value.contentDocument || previewIframe.value.contentWindow?.document

        if (!iframeDoc) return

        // Get the scrollHeight of the entire document
        const scrollHeight = Math.max(
            iframeDoc.body.scrollHeight,
            iframeDoc.documentElement.scrollHeight
        )

        // Add a small buffer to prevent scrollbars
        previewIframe.value.style.height = `${scrollHeight + 20}px`

        console.log(`Resized iframe to ${scrollHeight}px`)
    } catch (error) {
        console.error("Error resizing iframe:", error)
        // Fallback to a default height if we can't resize
        if (previewIframe.value) {
            previewIframe.value.style.height = "800px"
        }
    }
}

// Function to change view mode and trigger resize
function setViewMode(mode: string) {
    viewMode.value = mode
    // Use setTimeout to ensure the DOM has updated after the view mode change
    setTimeout(resizeIframe, 100)
}

// Resize on mount and when builder content changes
onMounted(() => {
    if (builder?.html) {
        setTimeout(resizeIframe, 200)
    }
    window.addEventListener('message', handlePreviewMessage)
})

onBeforeUnmount(() => {
    window.removeEventListener('message', handlePreviewMessage)
})

// Watch for changes to trigger iframe reload
watch(() => builder?.html, () => {
    if (builder?.html) {
        setTimeout(resizeIframe, 200)
    }
})

// Watch for sections/client changes to trigger iframe reload
watch([showSections, client], () => {
    setTimeout(resizeIframe, 200)
})
</script>
<style lang="scss" scoped>
.shot {
  display: flex;
  flex: 0 0 256px;
  flex-direction: column;
  gap: 8px;
}

.shot-img {
  display: block;
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.shot-note {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--muted);
  text-align: center;
}

.preview-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 14px 0;
}

.preview-well {
  position: relative;
  padding: 16px;
  border-radius: 14px;
  background: var(--well);
}

.email-preview {
  iframe {
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
